import type {SetIssueStatusMutationMutation} from '@github-ui/server-persisted-queries/types/setIssueStatusMutation'

type ConfirmedStatus = NonNullable<NonNullable<SetIssueStatusMutationMutation['setIssueStatus']>['issue']>['status']

export type IssueStatusOption = NonNullable<ConfirmedStatus>

type IssueStateData = {
  readonly state: string
  readonly stateReason?: string | null
}

/** A status whose category closes the issue as a duplicate needs a canonical issue to point at. */
export function needsDuplicateIssue(status: IssueStatusOption): boolean {
  return status.category.stateReason === 'DUPLICATE'
}

function fitsState(status: IssueStatusOption, issue: IssueStateData): boolean {
  if (status.category.state !== issue.state) return false
  return status.category.state === 'OPEN' || status.category.stateReason === (issue.stateReason ?? 'COMPLETED')
}

// The state can change without the status, like closing through the legacy close button, so a status
// only counts while it fits the state. Otherwise a closed issue falls back to the first status for its reason.
export function currentIssueStatus(
  issue: IssueStateData,
  statuses: readonly IssueStatusOption[],
  ...candidates: Array<IssueStatusOption | null | undefined>
): IssueStatusOption | undefined {
  const fitting = candidates.find(candidate => candidate && fitsState(candidate, issue))
  if (fitting) return fitting
  if (issue.state !== 'CLOSED') return undefined
  return statuses.find(status => fitsState(status, issue))
}

/**
 * The status the button offers when nothing is picked from the menu, like the Close and Reopen issue
 * buttons it replaces. An open issue closes as completed, whatever status it has. A closed issue
 * reopens to the first status in the Backlog category, where the server's own reopen lands. Without a
 * status of that kind there's no default, rather than one that does something else.
 */
export function defaultIssueStatus(
  issue: IssueStateData,
  statuses: readonly IssueStatusOption[],
): IssueStatusOption | undefined {
  if (issue.state === 'CLOSED') return statuses.find(status => status.category.key === 'backlog')
  return statuses.find(status => status.category.state === 'CLOSED' && status.category.stateReason === 'COMPLETED')
}
